"use client";

import { StyledLink } from "@/components/StyledLink";
import {
  Box,
  Container,
  Typography,
  Select,
  MenuItem,
  LinearProgress,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Stack,
  Link,
  CircularProgress,
  styled,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ExpandIcon as ExpandMore,
  ExternalLink,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import { apiRemote, IUserRoundStatus } from "@/api";
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#007bff',
    },
    secondary: {
      main: '#6c757d',
    },
    success: {
      main: '#28a745',
    },
    warning: {
      main: '#ffc107',
    },
    tcc: {
      main: '#6f42c1',
    },
    background: {
      default: '#f4f7f9',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h1: {
      fontSize: '28px',
      fontWeight: 700,
      color: '#333',
    },
    h3: {
      fontSize: '18px',
      fontWeight: 500,
    },
    h4: {
      fontSize: '16px',
      fontWeight: 500,
    },
    body1: {
      fontSize: '16px',
    },
    body2: {
      fontSize: '14px',
    },
    caption: {
      fontSize: '12px',
    },
  },
  shape: {
    borderRadius: 2,
  },
});

const StatusStep = styled(Box)(({ theme, completed, active }) => ({
  flex: 1,
  textAlign: 'center',
  fontSize: '12px',
  color: theme.palette.secondary.main,
  position: 'relative',
  '&::before': {
    content: '""',
    display: 'block',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#e9ecef',
    border: '2px solid #ced4da',
    margin: '0 auto 8px',
    ...(completed && {
      backgroundColor: '#6f42c1',
      borderColor: '#6f42c1',
    }),
    ...(active && {
      backgroundColor: '#fff',
      borderColor: '#6f42c1',
    }),
  },
  '&:not(:last-child)::after': {
    content: '""',
    position: 'absolute',
    top: '12px',
    left: '50%',
    width: '100%',
    height: '2px',
    backgroundColor: '#ced4da',
    zIndex: -1,
  },
  ...(completed &&
    {
      '&:not(:last-child)::after': {
        backgroundColor: '#6f42c1',
      },
    }),
}));

const WidgetHeader = ({ title, viewAllLink, titleColor }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #eee',
      paddingBottom: 1.5,
      marginBottom: 2,
    }}
  >
    <Typography variant="h5" sx={{ color: titleColor || 'text.primary' }}>
      {title}
    </Typography>
    {viewAllLink && (
      <Link href={viewAllLink} sx={{ fontSize: '14px', color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}>
        Go to TCC Champions Forum →
      </Link>
    )}
  </Box>
);

const TccWidget = () => (
  <Paper
    sx={{
      p: 3,
      borderRadius: theme.shape.borderRadius,
      boxShadow: 'none',
      bgcolor: '#f8f7fc',
      border: '1px solid #e0d8f5',
    }}
  >
    <WidgetHeader
      title="ThoughtCoCreate 2025"
      viewAllLink={process.env.API_COMM+"/groups/tcc-2025-champions/forum/"}
      titleColor="#6f42c1"
    />
    <Box sx={{ display: 'flex', gap: 1.25, listStyle: 'none', p: 0, mb: 2.5 }}>
      <StatusStep completed>
        <Typography>Round 1</Typography>
      </StatusStep>
      <StatusStep completed>
        <Typography>Round 2</Typography>
      </StatusStep>
      <StatusStep completed>
        <Typography>Judging</Typography>
      </StatusStep>
      <StatusStep active>
        <Typography>Summit</Typography>
      </StatusStep>
    </Box>
    <Typography sx={{ mb: 2 }}>
      The ThoughtCoCreate 2025 is now at the <b>Summit</b> stage! Join your peers in the TCC Champions 2025 Group Forum.
    </Typography>
    <Button
      variant="contained"
      href={process.env.API_COMM+"/groups/tcc-2025-champions/forum/"}x
      target="_blank"
      sx={{
        bgcolor: 'tcc.main',
        color: '#fff',
        padding: '10px 20px',
        borderRadius: '5px',
        fontWeight: 500,
        textTransform: 'none',
        '&:hover': { bgcolor: '#5a2ab4' },
      }}
    >
      Go to Group Forum
    </Button>
  </Paper>
);

export default function EducatorTCC() {
  const navigate = useNavigate();
  const [userCompetition, setUserCompetition] = useState<
    IUserRoundStatus["competition"] | null
  >(null);
  const [userRound, setUserRound] = useState<IUserRoundStatus["rounds"]>([]);
  const [userRoundParts, setUserRoundParts] = useState<
    IUserRoundStatus["round_parts"]
  >([]);

  const [loading, setLoading] = useState<boolean>(false);

  const loadUserRound = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRemote.getUserRoundStatus();
      if (response?.competition) {
        setUserCompetition(response.competition);
      }
      if (response?.rounds) {
        setUserRound(response.rounds);
      }
      if (response?.round_parts) {
        setUserRoundParts(response.round_parts);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserRound();
  }, []);

  const nextRound = userRound
    .filter((round) => new Date(round.due_date) >= new Date())
    .sort(
      (a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
    )[0];

  return (
    <>
    <TccWidget />
    </>
  );
}
